import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { usePlayer } from "@/context/PlayerContext";
import { Trophy, Loader2, ArrowLeft, Play, Pause } from "lucide-react";

export default function SoldArchive() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { current, isPlaying, playTrack } = usePlayer();

  useEffect(() => {
    api.get("/tracks/sold")
      .then((r) => setTracks(r.data.map((t) => ({ ...t, stream_url: `/teaser/${t.id}` }))))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen grain pb-32">
      <Navbar />

      <section className="px-6 md:px-12 pt-16 pb-8 border-b border-[#2a2a2a]">
        <Link to="/" data-testid="sold-back-link" className="inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#e2ff31] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to store
        </Link>
        <div className="flex items-center gap-3">
          <Trophy className="w-8 h-8 text-[#e2ff31]" />
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter">Sold Vault</h1>
        </div>
        <p className="text-[#9ca3af] mt-3 max-w-xl leading-relaxed">
          Every beat here was claimed exclusively by an artist — a record of the sound that's already out there working.
        </p>
        <p className="text-sm font-mono text-[#6b7280] mt-2" data-testid="sold-count">{tracks.length} {tracks.length === 1 ? "beat" : "beats"} sold</p>
      </section>

      <section className="px-6 md:px-12 py-12">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#e2ff31]" /></div>
        ) : tracks.length === 0 ? (
          <div data-testid="sold-empty" className="text-center py-24 border border-dashed border-[#2a2a2a] rounded-2xl">
            <Trophy className="w-12 h-12 text-[#2a2a2a] mx-auto mb-4" />
            <p className="text-[#9ca3af]">No beats sold yet — your first exclusive is coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="sold-grid">
            {tracks.map((t) => {
              const isCurrent = current?.id === t.id;
              return (
                <div key={t.id} data-testid={`sold-${t.id}`} className="group relative bg-[#0f0f0f] border border-[#1e1e1e] rounded-2xl overflow-hidden hover:border-[#e2ff31]/30 transition-colors">
                  <div className="relative aspect-square bg-[#1e1e1e]">
                    <img src={`${API}${t.cover_url}`} alt={t.title} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="w-full h-full object-cover grayscale-[35%]" />
                    <div className="absolute inset-0 bg-black/30" />
                    <span className="absolute top-3 left-3 rotate-[-8deg] text-xs font-bold uppercase tracking-widest text-[#ef4444] border-2 border-[#ef4444] rounded px-2 py-0.5 bg-black/70">Sold</span>
                    <button data-testid={`sold-teaser-${t.id}`} onClick={() => playTrack(t, tracks)}
                      aria-label={`Play 30 second preview of ${t.title}`}
                      className={`absolute inset-0 flex flex-col items-center justify-center gap-2 transition-opacity ${isCurrent ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                      <span className="w-12 h-12 rounded-full bg-[#e2ff31] text-black flex items-center justify-center">
                        {isCurrent && isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest bg-black/70 rounded-full px-2 py-0.5">30s preview</span>
                    </button>
                  </div>
                  <div className="p-3">
                    <p className={`font-medium truncate ${isCurrent ? "text-[#e2ff31]" : ""}`}>{t.title}</p>
                    <div className="flex items-center justify-between text-xs text-[#6b7280] mt-1">
                      <span className="truncate">{t.producer}</span>
                      <span className="font-mono shrink-0 ml-2">{t.genre}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
